import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import PageHeader from '@/components/PageHeader';
import CtaBand from '@/components/CtaBand';
import TrustRow from '@/components/TrustRow';
import { Button } from '@/components/ui/button';
import { Building2, Briefcase, Hospital, Store, Warehouse, Hotel, ArrowRight } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const SECTORS = [
  { icon: Briefcase, name: 'Offices', desc: 'HVAC, cleaning, painting, electrical maintenance.' },
  { icon: Store, name: 'Retail', desc: 'Storefront refresh, deep clean, signage, repairs.' },
  { icon: Hospital, name: 'Medical Offices', desc: 'Compliance-grade cleaning + handyman.' },
  { icon: Warehouse, name: 'Warehouses', desc: 'Epoxy floors, pressure wash, HVAC.' },
  { icon: Hotel, name: 'HOAs & Apartments', desc: 'Common-area maintenance + landscaping.' },
  { icon: Building2, name: 'Property Mgmt', desc: 'One vendor across your entire portfolio.' },
];

export default function CommercialPage() {
  const { user } = useAuth();
  const isCommercial = user?.role === 'commercial';
  const [commercialCat, setCommercialCat] = useState(null);
  useEffect(() => { api.get('/services/commercial').then(r => setCommercialCat(r.data)).catch(() => {}); }, []);
  return (
    <Layout>
      <PageHeader eyebrow="For Businesses & Properties" title="One vendor for every facility you manage." subtitle="Royal Palm Services handles recurring maintenance and project work for HOAs, offices, retail, restaurants, medical offices, and more across 5 South Florida counties.">
        <TrustRow />
      </PageHeader>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SECTORS.map(s => (
            <div key={s.name} className="rounded-2xl border border-[#E6E9EE] bg-white p-5">
              <div className="h-10 w-10 rounded-xl bg-[#F2F7F5] border border-[#C7DED4] flex items-center justify-center"><s.icon className="h-5 w-5 text-[#17352D]" /></div>
              <div className="mt-3 font-headline text-xl text-[#17352D]">{s.name}</div>
              <div className="text-sm text-slate-600 mt-1">{s.desc}</div>
            </div>
          ))}
        </div>
      </section>
      {commercialCat && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-headline text-3xl text-[#17352D]">Commercial service catalog</h2>
            <Link to="/services/commercial" className="text-sm font-semibold text-[#17352D] inline-flex items-center gap-1">All commercial services <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {commercialCat.sub_services.map(s => (
              <div key={s.slug} className="flex items-center justify-between gap-3 rounded-xl border border-[#E6E9EE] bg-white p-3">
                <div><div className="font-medium text-[#17352D]">{s.name}</div><div className="text-xs text-slate-500">From ${s.price_low.toLocaleString()} – ${s.price_high.toLocaleString()}</div></div>
                <Button asChild size="sm" className="rounded-lg bg-[#C8922F] hover:bg-[#A87424] text-white"><Link to={`/estimate?category=commercial&sub=${s.slug}`}>Get Estimate</Link></Button>
              </div>
            ))}
          </div>
        </section>
      )}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-3xl border border-[#EED7A6] bg-[#FBF7EE] p-8 grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#C8922F] font-semibold">{isCommercial ? 'Welcome back' : 'Property Manager?'}</div>
            <h3 className="mt-1 font-headline text-2xl md:text-3xl text-[#17352D]">{isCommercial ? (user.company || user.name) : 'Use our Commercial Client Portal'}</h3>
            <p className="text-slate-700 mt-2">{isCommercial ? 'Jump back into your portal to manage properties, submit work orders and track service.' : 'Track multiple properties, submit work orders, view service history, and manage invoices — all in one dashboard.'}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
            {isCommercial ? (
              <Button asChild className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white h-12 px-6" data-testid="commercial-goto-portal">
                <Link to="/commercial-portal">Go to my portal <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            ) : (
              <>
                <Button asChild className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white h-12 px-6"><Link to="/signup?role=commercial">Open Commercial Account</Link></Button>
                <Button asChild variant="outline" className="rounded-xl h-12 px-6"><Link to="/login">Sign in</Link></Button>
              </>
            )}
          </div>
        </div>
      </section>
      <CtaBand title="Need a commercial vendor you can trust?" />
    </Layout>
  );
}
